// Lightweight, dependency-free confetti burst.
//
// Spawns a short-lived full-screen <canvas> overlay, animates a few hundred
// paper bits flying up and fluttering down under gravity, then removes itself.
// No library, no persistent canvas — call fireConfetti() and forget. Used on
// the public campaign page when a new donation lands (opt-in per campaign).

interface ConfettiOptions {
  /** Horizontal origin as a 0..1 fraction of viewport width (default 0.5). */
  originX?: number;
  /** Vertical origin as a 0..1 fraction of viewport height (default 0.35). */
  originY?: number;
  /** Number of particles (default 140). */
  count?: number;
  /** Particle colors. */
  colors?: string[];
}

const DEFAULT_COLORS = ['#2563eb', '#16a34a', '#f59e0b', '#e11d48', '#7c3aed', '#0891b2'];

// Guard so a burst of donations in one tick doesn't stack dozens of canvases.
let active = false;

export function fireConfetti(opts: ConfettiOptions = {}): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (active) return;
  // Respect users who asked for reduced motion.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  active = true;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const w = window.innerWidth;
  const h = window.innerHeight;
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(w * dpr);
  canvas.height = Math.floor(h * dpr);
  Object.assign(canvas.style, {
    position: 'fixed', inset: '0', width: '100%', height: '100%',
    pointerEvents: 'none', zIndex: '2147483646',
  } as CSSStyleDeclaration);
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) { canvas.remove(); active = false; return; }
  ctx.scale(dpr, dpr);

  const colors = opts.colors && opts.colors.length ? opts.colors : DEFAULT_COLORS;
  const count = opts.count ?? 140;
  const ox = (opts.originX ?? 0.5) * w;
  const oy = (opts.originY ?? 0.35) * h;

  const particles = Array.from({ length: count }, (_, i) => {
    // Spread upward-and-outward; vary by index (no Math.random dependency on
    // determinism — randomness here is purely cosmetic and fine).
    const angle = (-Math.PI / 2) + (Math.random() - 0.5) * Math.PI * 0.9;
    const speed = 7 + Math.random() * 9;
    return {
      x: ox + (Math.random() - 0.5) * 40,
      y: oy + (Math.random() - 0.5) * 20,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      size: 5 + Math.random() * 6,
      color: colors[i % colors.length],
      rot: Math.random() * Math.PI,
      vrot: (Math.random() - 0.5) * 0.3,
      drift: (Math.random() - 0.5) * 0.6,
      life: 0,
    };
  });

  const GRAVITY = 0.22;
  const DRAG = 0.985;
  const MAX_FRAMES = 200; // ~3.3s at 60fps
  let frame = 0;
  let raf = 0;

  const tick = () => {
    frame++;
    ctx.clearRect(0, 0, w, h);
    let alive = 0;
    for (const p of particles) {
      p.vx = p.vx * DRAG + p.drift * 0.05;
      p.vy = p.vy * DRAG + GRAVITY;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vrot;
      p.life++;
      const fade = Math.max(0, 1 - (frame - 120) / 80); // start fading after frame 120
      if (p.y < h + 20 && fade > 0) {
        alive++;
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
    }
    if (alive > 0 && frame < MAX_FRAMES) {
      raf = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(raf);
      canvas.remove();
      active = false;
    }
  };
  raf = requestAnimationFrame(tick);
}

// Guard so we never run two goal celebrations at once (e.g. a re-render or a
// double poll-cross). The first call owns the screen until it finishes.
let celebrating = false;

interface CelebrationOptions {
  /** How long to keep raining confetti, in ms (default 120000 = 2 minutes). */
  durationMs?: number;
  /** Particle colors. */
  colors?: string[];
}

// Sustained, full-screen confetti rain for a goal-reached celebration.
//
// Unlike fireConfetti() (one short upward burst), this keeps a single canvas
// alive and steadily spawns particles from the top edge across the full width
// for `durationMs`, then lets the last pieces fall before tearing down. Tuned
// to read as "lots of confetti for ~2 minutes" without choking the main thread.
export function fireConfettiCelebration(opts: CelebrationOptions = {}): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (celebrating) return;
  // Respect users who asked for reduced motion.
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
  celebrating = true;

  const durationMs = opts.durationMs ?? 120000;
  const colors = opts.colors && opts.colors.length ? opts.colors : DEFAULT_COLORS;

  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  let w = window.innerWidth;
  let h = window.innerHeight;
  const canvas = document.createElement('canvas');
  canvas.width = Math.floor(w * dpr);
  canvas.height = Math.floor(h * dpr);
  Object.assign(canvas.style, {
    position: 'fixed', inset: '0', width: '100%', height: '100%',
    pointerEvents: 'none', zIndex: '2147483646',
  } as CSSStyleDeclaration);
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) { canvas.remove(); celebrating = false; return; }
  ctx.scale(dpr, dpr);

  const onResize = () => {
    w = window.innerWidth;
    h = window.innerHeight;
    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.scale(dpr, dpr);
  };
  window.addEventListener('resize', onResize);

  interface Particle {
    x: number; y: number; vx: number; vy: number; size: number;
    color: string; rot: number; vrot: number; drift: number;
  }
  const particles: Particle[] = [];
  // Spawn a steady ~10 pieces per frame from random points along the top edge,
  // each drifting down and sideways. At 60fps that's ~600/sec — dense rain.
  const spawn = () => {
    const n = 10;
    for (let i = 0; i < n; i++) {
      particles.push({
        x: Math.random() * w,
        y: -20 - Math.random() * 40,
        vx: (Math.random() - 0.5) * 3,
        vy: 2 + Math.random() * 3,
        size: 5 + Math.random() * 6,
        color: colors[(particles.length + i) % colors.length],
        rot: Math.random() * Math.PI,
        vrot: (Math.random() - 0.5) * 0.3,
        drift: (Math.random() - 0.5) * 0.6,
      });
    }
  };

  const GRAVITY = 0.12;
  const DRAG = 0.99;
  // Stamp the wall-clock start once (Date.now is unavailable in some sandboxes,
  // but this is browser-only UI code where it's always present).
  const start = Date.now();
  let raf = 0;

  const tick = () => {
    const elapsed = Date.now() - start;
    if (elapsed < durationMs) spawn();
    ctx.clearRect(0, 0, w, h);
    let alive = 0;
    for (const p of particles) {
      p.vx = p.vx * DRAG + p.drift * 0.05;
      p.vy = p.vy * DRAG + GRAVITY;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.vrot;
      if (p.y < h + 20) {
        alive++;
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
        ctx.restore();
      }
    }
    // Drop off-screen particles so the array doesn't grow unbounded over 2 min.
    if (particles.length > 1200) {
      for (let i = particles.length - 1; i >= 0; i--) {
        if (particles[i].y > h + 20) particles.splice(i, 1);
      }
    }
    if (elapsed < durationMs || alive > 0) {
      raf = requestAnimationFrame(tick);
    } else {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      canvas.remove();
      celebrating = false;
    }
  };
  raf = requestAnimationFrame(tick);
}
